import React, { useState, useEffect, useRef } from 'react';
import { Clock, AlertTriangle, RefreshCw, LogOut, X } from 'lucide-react';
import apiService from '../../services/api';
import useActivityTracker from '../../hooks/useActivityTracker';
import BaseModal from './BaseModal';

const SessionWarning = ({ onSessionExtended, onLogout }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [extending, setExtending] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [extensionError, setExtensionError] = useState('');
  const { isActive } = useActivityTracker();
  const lastAutoExtendRef = useRef(0);

  useEffect(() => {
    const checkTokenExpiration = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        // Decode JWT token to get expiration time
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) return;
        
        const payload = JSON.parse(atob(tokenParts[1]));
        const exp = payload.exp * 1000; // Convert to milliseconds
        const now = Date.now();
        const timeLeft = exp - now;
        
        // Show warning 5 minutes (300000ms) before expiration
        const warningThreshold = 5 * 60 * 1000; // 5 minutes
        
        if (timeLeft <= warningThreshold && timeLeft > 0) {
          // Check if user is active
          if (isActive()) {
            // User is active - auto-extend session silently using refresh token
            const timeSinceLastAutoExtend = now - lastAutoExtendRef.current;

            // Only auto-extend once every 5 minutes to avoid too many refreshes
            if (timeSinceLastAutoExtend > 5 * 60 * 1000) {

              try {
                // Refresh the access token using refresh token
                await apiService.extendSession();

                lastAutoExtendRef.current = now;
                setShowWarning(false);
                onSessionExtended?.();
              } catch (error) {
                // If auto-extend fails, show the warning
                setShowWarning(true);
                setTimeRemaining(Math.ceil(timeLeft / 1000));
              }
            }
          } else {
            // User is inactive - show warning
            setShowWarning(true);
            setTimeRemaining(Math.ceil(timeLeft / 1000)); // Convert to seconds
          }
        } else if (timeLeft <= 0) {
          // Token has expired
          setShowWarning(false);
          onLogout?.();
        } else {
          setShowWarning(false);
        }
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    };

    // Listen for authentication failures from API calls
    const handleAuthFailure = (event) => {
      setShowWarning(true);
      setTimeRemaining(0); // Show immediate expiration
    };

    // Check immediately
    checkTokenExpiration();

    // Check every 30 seconds
    const interval = setInterval(checkTokenExpiration, 30000);
    
    // Listen for auth failures
    window.addEventListener('authenticationFailed', handleAuthFailure);

    return () => {
      clearInterval(interval);
      window.removeEventListener('authenticationFailed', handleAuthFailure);
    };
  }, [onLogout, isActive, onSessionExtended]);

  // Separate useEffect for countdown timer - updates every second when warning is shown
  useEffect(() => {
    if (!showWarning) return;

    const countdownInterval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (!token) {
        setTimeRemaining(0);
        return;
      }

      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) return;
        
        const payload = JSON.parse(atob(tokenParts[1]));
        const exp = payload.exp * 1000;
        const now = Date.now();
        const timeLeft = Math.max(0, exp - now);
        
        const secondsLeft = Math.ceil(timeLeft / 1000);
        setTimeRemaining(secondsLeft);
        
        // Auto-logout when timer reaches 0
        if (secondsLeft <= 0) {
          onLogout?.();
        }
      } catch (error) {
        console.error('Error updating countdown:', error);
        setTimeRemaining(0);
      }
    }, 1000); // Update every second

    return () => clearInterval(countdownInterval);
  }, [showWarning, onLogout]);

  const handleExtendSession = async () => {
    // If no password field shown yet, show it
    if (!showPasswordField) {
      setShowPasswordField(true);
      setExtensionError('');
      return;
    }
    
    // If password is empty, show error
    if (!password) {
      setExtensionError('Please enter your password');
      return;
    }
    
    setExtending(true);
    setExtensionError('');
    
    try {
      // Get the current username from localStorage or token
      const token = localStorage.getItem('token');
      let username = 'admin'; // Default fallback
      
      if (token) {
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            username = payload.sub || payload.username || 'admin';
          }
        } catch (e) {
          console.error('Error parsing token for username:', e);
        }
      }
      
      // Re-authenticate with username and password to get new token
      await apiService.login(username, password);
      
      setShowWarning(false);
      setShowPasswordField(false);
      setPassword('');
      onSessionExtended?.();
    } catch (error) {
      console.error('Failed to extend session:', error);
      setExtensionError('Invalid password. Please try again.');
    } finally {
      setExtending(false);
    }
  };

  const handleDismiss = () => {
    setShowWarning(false);
  };

  const handleLogoutNow = () => {
    setShowWarning(false);
    onLogout?.();
  };

  const formatTimeRemaining = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <BaseModal
      isOpen={showWarning}
      onClose={() => {}} // Prevent closing by clicking backdrop or ESC
      size="md"
      showCloseButton={false}
      closeOnBackdrop={false}
    >
      <div className="p-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-yellow-600" />
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {timeRemaining <= 0 ? 'Session Expired' : 'Session Expiring Soon'}
            </h3>
            
            <div className="mb-4">
              <div className="flex items-center space-x-2 text-gray-600 mb-2">
                <Clock className="w-4 h-4" />
                <span>
                  {timeRemaining <= 0 
                    ? 'Session has expired' 
                    : `Time remaining: ${formatTimeRemaining(timeRemaining)}`
                  }
                </span>
              </div>
              
              <p className="text-sm text-gray-600">
                {timeRemaining <= 0 
                  ? 'Your session has expired. Please extend your session to continue or log out.'
                  : 'Your session will expire soon. Would you like to extend your session or log out now?'
                }
              </p>
            </div>

            {showPasswordField && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter your password to extend session:
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleExtendSession();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Password"
                  autoFocus
                />
                {extensionError && (
                  <p className="mt-2 text-sm text-red-600">{extensionError}</p>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleExtendSession}
                disabled={extending}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {extending ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span>
                  {extending ? 'Authenticating...' : 
                   showPasswordField ? 'Confirm Extension' : 
                   'Extend Session'}
                </span>
              </button>
              
              <button
                onClick={handleLogoutNow}
                className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <LogOut className="w-4 h-4" />
                <span>Log Out Now</span>
              </button>
            </div>

            <button
              type="button"
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Dismiss warning"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default SessionWarning;